// frontend/src/components/Layout.jsx
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/auth';
import api, { impersonationApi } from '../utils/api';
import {
  LayoutDashboard, Calendar, CreditCard, Users, Package,
  FileText, Timer, Settings, LogOut, ChevronRight,
  DoorOpen, Bell, Building2, Tag, MapPin, Shield, Menu, X, Gift, ShoppingCart, Palette, KeyRound, AlertTriangle, HelpCircle, Award, ShieldAlert, Percent
} from 'lucide-react';
import { useState, useEffect } from 'react';
import PlanModal from './PlanModal';
import BrandIcon from './BrandIcon';
import WelcomeGuide from './WelcomeGuide';
import toast from 'react-hot-toast';

// ── Code support (connexion "en tant qu'utilisateur") ───────────
// L'utilisateur génère lui-même un code à 6 chiffres, valable 5 minutes, et
// le communique oralement au support — c'est ce code, jamais son mot de
// passe, qui autorise le SuperAdmin à se connecter à sa place.
function SupportCodeModal({ onClose }) {
  const [code, setCode] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const r = await impersonationApi.generate();
      setCode(r.data.code);
      setSecondsLeft(300);
    } catch (e) { toast.error("Erreur lors de la génération du code"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const t = setInterval(() => setSecondsLeft(s => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [secondsLeft]);

  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, '0');
  const ss = String(secondsLeft % 60).padStart(2, '0');

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 380, textAlign: 'center' }}>
        <h3 style={{ marginTop: 0 }}>Code support</h3>
        <p style={{ color: 'var(--text-3)', fontSize: 13, lineHeight: 1.6 }}>
          Communiquez ce code, oralement, uniquement à un membre du support EscapeOS
          que vous avez contacté vous-même. Il ne remplace jamais votre mot de passe
          et expire automatiquement après 5 minutes.
        </p>
        {code ? (
          <>
            <div style={{ fontSize: 36, fontWeight: 800, letterSpacing: '0.15em',
              fontFamily: 'monospace', margin: '16px 0 4px' }}>{code}</div>
            <div style={{ fontSize: 12, color: secondsLeft > 0 ? 'var(--text-3)' : 'var(--red)' }}>
              {secondsLeft > 0 ? `Expire dans ${mm}:${ss}` : 'Code expiré'}
            </div>
            <button className="btn btn-secondary btn-sm" style={{ marginTop: 14 }} onClick={generate} disabled={loading}>
              Générer un nouveau code
            </button>
          </>
        ) : (
          <button className="btn btn-primary" style={{ marginTop: 14 }} onClick={generate} disabled={loading}>
            {loading ? 'Génération…' : 'Générer un code'}
          </button>
        )}
        <button className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center', marginTop: 16 }}
          onClick={onClose}>Fermer</button>
      </div>
    </div>
  );
}

// ── Navigation groupée ────────────────────────────────────────
const NAV_GROUPS = [
  {
    label: 'Exploitation',
    items: [
      { to:'/app',              label:'Tableau de bord', icon:LayoutDashboard, end:true },
      { to:'/app/reservations', label:'Réservations',    icon:Calendar,   perm:'bookings.view',  feature:'bookings', tourId:'nav-reservations' },
      { to:'/app/timer',        label:'Timer Salles',    icon:Timer,      perm:'timer.manage',   feature:'timer' },
      { to:'/app/clients',      label:'Clients',         icon:Users,      perm:'customers.view', feature:'customers', tourId:'nav-clients' },
    ]
  },
  {
    label: 'Finance',
    items: [
      { to:'/app/caisse',      label:'Caisse',      icon:CreditCard,    perm:'cash.view',   feature:'cash', tourId:'nav-caisse' },
      // Stock, Happy Hours et Commandes à table sont désormais des onglets
      // internes du Mode caisse (c'est là qu'ils servent au quotidien) —
      // plus besoin de leur propre entrée de menu.
      { to:'/app/caisse-pos',   label:'Mode caisse', icon:ShoppingCart,  perm:'pos.access', tourId:'nav-pos' },
      { to:'/app/rapports', label:'Rapports', icon:FileText,   perm:'reports.view', feature:'reports' },
    ]
  },
  {
    label: 'RH & Planning',
    items: [
      { to:'/app/planning', label:'Planning RH', icon:Users, perm:'schedule.view', feature:'schedule', tourId:'nav-planning' },
    ]
  },
];

const ADMIN_GROUPS = [
  {
    label: 'Gestion',
    items: [
      { to:'/app/admin/salles',        label:'Salles & Tarifs',    icon:DoorOpen, min:'manager', perm:'admin.rooms', tourId:'nav-admin' },
      { to:'/app/admin/sites',         label:'Sites & Établissements', icon:MapPin, min:'admin', perm:'admin.sites' },
      { to:'/app/admin/gift-cards',    label:'Bons cadeaux',       icon:Gift,     min:'staff' },
      { to:'/app/admin/fidelite',      label:'Fidélité',           icon:Award,    min:'admin' },
      { to:'/app/admin/partenaires',   label:'Partenaires',        icon:Percent,  min:'manager' },
      { to:'/app/admin/remboursements', label:'Remboursements',     icon:CreditCard, min:'manager', badge:'refunds' },
      { to:'/app/admin/notifications', label:'Notifications',      icon:Bell,     min:'manager' },
      { to:'/app/admin/audit',         label:'Journal d\'audit',    icon:ShieldAlert, min:'admin' },
    ]
  },
  {
    label: 'Configuration',
    items: [
      { to:'/app/admin/equipe',     label:'Équipe & Accès',     icon:Users,    min:'admin', perm:'admin.users' },
      // Moyens de paiement, Profils tarifaires et Périodes tarifaires sont
      // regroupés en onglets internes de la page Tarification.
      { to:'/app/admin/tarification', label:'Tarification',    icon:Tag,      min:'manager', perm:'admin.pricing' },
      { to:'/app/admin/parametres', label:'Paramètres',         icon:Settings, min:'admin', perm:'admin.settings' },
    ]
  },
];

function NavGroup({ group, can, features, onClick, badgeCounts }) {
  const visible = group.items.filter(n => {
    if (n.feature && Object.keys(features).length > 0) {
      const feat = features[n.feature];
      if (feat && !feat.enabled) return false;
    }
    if (n.perm && !can(n.perm)) return false;
    if (n.min && !can(n.min)) return false;
    return true;
  });
  if (!visible.length) return null;
  return (
    <>
      <div className="nav-section-title">{group.label}</div>
      {visible.map(({ to, label, icon: Icon, end, tourId, badge }) => {
        const count = badge ? (badgeCounts?.[badge] || 0) : 0;
        return (
          <NavLink key={to} to={to} end={end} onClick={onClick} data-tour={tourId}
            className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
            style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <span style={{ display:'flex', alignItems:'center', gap:8 }}>
              <Icon size={16}/> {label}
            </span>
            {count > 0 && (
              <span style={{
                minWidth:18, height:18, padding:'0 5px', borderRadius:9,
                background:'var(--red)', color:'#fff', fontSize:11, fontWeight:700,
                display:'flex', alignItems:'center', justifyContent:'center',
              }}>
                {count > 99 ? '99+' : count}
              </span>
            )}
          </NavLink>
        );
      })}
    </>
  );
}

export default function Layout() {
  const { user, logout, can, features } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawer] = useState(false);
  const [showPlan, setShowPlan] = useState(false);
  const [showSupportCode, setShowSupportCode] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [badgeCounts, setBadgeCounts] = useState({});
  const impersonating = localStorage.getItem('eg_impersonating') === '1';

  // Pastille "Remboursements" : nombre de demandes en attente de validation
  // — sinon un manager peut ne jamais remarquer qu'une demande traîne (ex:
  // trop-perçu créé depuis une fiche résa) tant qu'il ne clique pas dessus.
  // Rafraîchi périodiquement plutôt qu'en push, plus simple qu'un websocket
  // dédié pour un simple compteur.
  useEffect(() => {
    if (!can('manager')) return;
    const loadRefundCount = () => {
      api.get('/refund-requests', { params: { status: 'pending' } })
        .then(r => setBadgeCounts(c => ({ ...c, refunds: (r.data.refundRequests || []).length })))
        .catch(() => {});
    };
    loadRefundCount();
    const t = setInterval(loadRefundCount, 60000);
    return () => clearInterval(t);
  }, [can]);

  const initials = user ? `${user.first_name[0]}${user.last_name[0]}` : '?';
  const closeDrawer = () => setDrawer(false);

  // Tutoriel d'accueil : affiché automatiquement une seule fois par
  // utilisateur (clé localStorage dédiée, pas de champ backend) — jamais en
  // pleine session d'impersonation support, pour ne pas gêner le support.
  useEffect(() => {
    if (!user?.id || impersonating) return;
    const key = `eg_onboarding_seen_${user.id}`;
    if (!localStorage.getItem(key)) setShowWelcome(true);
  }, [user?.id, impersonating]);

  const dismissWelcome = () => {
    if (user?.id) localStorage.setItem(`eg_onboarding_seen_${user.id}`, '1');
    setShowWelcome(false);
  };

  // Le raccourci SuperAdmin n'est proposé que si l'email du user
  // correspond à un compte super_admin actif (vérifié côté backend)
  useEffect(() => {
    if (can('admin')) {
      api.get('/auth/is-superadmin').then(r => setIsSuperAdmin(!!r.data.isSuperAdmin)).catch(() => {});
    }
  }, [can]);

  const SidebarContent = ({ onNavClick }) => (
    <>
      {/* Logo */}
      <div className="sidebar-logo">
        <h1 style={{ display:'flex', alignItems:'center', gap:8 }}><BrandIcon size={24}/> EscapeOS</h1>
      </div>

      {/* Nav principale groupée */}
      <nav className="sidebar-nav">
        {NAV_GROUPS.map(g => (
          <NavGroup key={g.label} group={g} can={can} features={features} onClick={onNavClick} badgeCounts={badgeCounts}/>
        ))}

        {can('manager') && (
          <>
            <div className="nav-section-title" style={{marginTop:8}}>Administration</div>
            {ADMIN_GROUPS.map(g => (
              <NavGroup key={g.label} group={g} can={can} features={features} onClick={onNavClick} badgeCounts={badgeCounts}/>
            ))}
          </>
        )}

        <div className="nav-section-title" style={{marginTop:8}}>Liens</div>
        <a className="nav-item" href="/reserver" target="_blank" rel="noreferrer">
          <Building2 size={16}/> Planning public <ChevronRight size={12} style={{marginLeft:'auto'}}/>
        </a>
        {isSuperAdmin && (
          <a className="nav-item" href="/superadmin" target="_blank" rel="noreferrer">
            <Shield size={16}/> Super Admin <ChevronRight size={12} style={{marginLeft:'auto'}}/>
          </a>
        )}
      </nav>

      {/* Footer */}
      <div className="sidebar-footer">
        <button onClick={() => { setShowWelcome(true); closeDrawer(); }}
          style={{ width:'100%', padding:'8px 12px', marginBottom:8, borderRadius:8,
            background:'rgba(124,92,252,0.08)', border:'1px solid rgba(124,92,252,0.2)',
            color:'var(--accent-light)', cursor:'pointer', fontFamily:'inherit',
            fontSize:12, fontWeight:600, display:'flex', alignItems:'center', gap:6 }}>
          <HelpCircle size={13}/> Aide & tutoriel
        </button>
        <button onClick={() => { setShowSupportCode(true); closeDrawer(); }}
          style={{ width:'100%', padding:'8px 12px', marginBottom:8, borderRadius:8,
            background:'rgba(0,210,180,0.08)', border:'1px solid rgba(0,210,180,0.2)',
            color:'var(--teal)', cursor:'pointer', fontFamily:'inherit',
            fontSize:12, fontWeight:600, display:'flex', alignItems:'center', gap:6 }}>
          <KeyRound size={13}/> Code support
        </button>
        {can('admin') && (
          <button onClick={() => { setShowPlan(true); closeDrawer(); }}
            style={{ width:'100%', padding:'8px 12px', marginBottom:8, borderRadius:8,
              background:'rgba(124,92,252,0.1)', border:'1px solid rgba(124,92,252,0.2)',
              color:'var(--accent-light)', cursor:'pointer', fontFamily:'inherit',
              fontSize:12, fontWeight:600, display:'flex', alignItems:'center', gap:6 }}>
            <Package size={13}/> Mon abonnement
          </button>
        )}
        <div className="user-chip" onClick={() => { navigate('/app/admin/utilisateurs'); closeDrawer(); }}>
          <div className="avatar">{initials}</div>
          <div className="user-chip-info">
            <strong>{user?.first_name} {user?.last_name}</strong>
            <span>{user?.role}</span>
          </div>
          <button className="btn-ghost"
            onClick={e => { e.stopPropagation(); logout(); navigate('/login'); }}
            title="Déconnexion">
            <LogOut size={15}/>
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="app-layout">

      {/* ── Sidebar desktop ────────────────────────────────────── */}
      <aside className="sidebar">
        <SidebarContent onNavClick={null}/>
      </aside>

      {/* ── Header mobile ──────────────────────────────────────── */}
      <header style={{
        display:'none', position:'fixed', top:0, left:0, right:0, zIndex:50,
        height:54, background:'var(--bg-card)', borderBottom:'1px solid var(--border)',
        alignItems:'center', padding:'0 16px', gap:12,
      }} className="mobile-header">
        <button onClick={() => setDrawer(true)}
          style={{ background:'none', border:'none', cursor:'pointer',
            color:'var(--text-1)', padding:6, borderRadius:8,
            display:'flex', alignItems:'center' }}>
          <Menu size={22}/>
        </button>
        <span style={{ fontWeight:800, fontSize:16, flex:1, display:'flex', alignItems:'center', gap:8 }}>
          <BrandIcon size={20}/>
          <span style={{
            background:'linear-gradient(135deg,var(--accent-light),var(--teal))',
            WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>
            EscapeOS
          </span>
        </span>
        <button onClick={() => setShowWelcome(true)} title="Aide & tutoriel"
          style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-2)',
            padding:6, display:'flex', alignItems:'center' }}>
          <HelpCircle size={19}/>
        </button>
        <div className="avatar" style={{ width:30, height:30, fontSize:11 }}
          onClick={() => navigate('/app/admin/utilisateurs')}>
          {initials}
        </div>
      </header>

      {/* ── Drawer mobile ──────────────────────────────────────── */}
      {drawerOpen && (
        <div style={{
          position:'fixed', inset:0, zIndex:200,
          background:'rgba(0,0,0,0.6)', backdropFilter:'blur(4px)',
        }} onClick={closeDrawer}>
          <div style={{
            position:'absolute', top:0, left:0, bottom:0, width:280,
            background:'var(--bg-card)', display:'flex', flexDirection:'column',
            overflowY:'auto', boxShadow:'4px 0 20px rgba(0,0,0,0.4)',
          }} onClick={e => e.stopPropagation()}>
            {/* Header drawer */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
              padding:'16px 16px 0', marginBottom:8 }}>
              <span style={{ fontWeight:800, fontSize:16, display:'flex', alignItems:'center', gap:8 }}><BrandIcon size={20}/> EscapeOS</span>
              <button onClick={closeDrawer}
                style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-3)' }}>
                <X size={20}/>
              </button>
            </div>
            <div style={{ flex:1, overflow:'auto' }}>
              <SidebarContent onNavClick={closeDrawer}/>
            </div>
          </div>
        </div>
      )}

      {showPlan && <PlanModal onClose={() => setShowPlan(false)}/>}
      {showSupportCode && <SupportCodeModal onClose={() => setShowSupportCode(false)}/>}
      {showWelcome && (
        <WelcomeGuide onClose={dismissWelcome} can={can}
          openDrawer={() => setDrawer(true)} closeDrawer={closeDrawer}/>
      )}

      <main className="main-content">
        {impersonating && (
          <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 16px',
            background:'rgba(245,166,35,0.12)', borderBottom:'1px solid rgba(245,166,35,0.3)',
            color:'var(--amber)', fontSize:13, fontWeight:600 }}>
            <AlertTriangle size={15}/>
            Session support — vous êtes connecté en tant que {user?.email}
            <button onClick={() => { logout(); }}
              style={{ marginLeft:'auto', background:'none', border:'1px solid var(--amber)',
                color:'var(--amber)', borderRadius:8, padding:'4px 10px', cursor:'pointer',
                fontFamily:'inherit', fontSize:12, fontWeight:700 }}>
              Quitter la session
            </button>
          </div>
        )}
        <Outlet/>
      </main>
    </div>
  );
}
